import React, { useState, useCallback, useEffect } from "react";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { offerService } from "../services/apiService";

const PAGE_SIZE = 10;

const OfferSearch = () => {
  const [searchTitle, setSearchTitle] = useState("");
  const [titleOptions, setTitleOptions] = useState([]);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Selected offer's send history
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const showMsg = (type, text) => setToast({ type, message: text });

  const handleSearch = useCallback(async (pageNo = 0) => {
    setLoading(true);
    setSearched(true);
    setSelectedOffer(null);
    try {
      const data = await offerService.searchOffers({
        title: searchTitle.trim(),
        page: pageNo,
        size: PAGE_SIZE,
      });
      const list = data.content ?? [];
      setResults(list);
      setPage(data.page ?? pageNo);
      setTotalPages(data.totalPages ?? 0);
      setTotalElements(data.totalElements ?? list.length);
      if (!list.length) showMsg("error", "No offers found for the given criteria");
    } catch (err) {
      setResults([]);
      showMsg("error", err.response?.data?.errorMessage || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [searchTitle]);

  // Populate the title dropdown with distinct titles from this client's offers
  // via the dedicated lightweight endpoint (SELECT DISTINCT title).
  const loadTitleOptions = useCallback(async () => {
    try {
      const titles = await offerService.getOfferTitles();
      setTitleOptions(Array.isArray(titles) ? titles : []);
    } catch {
      setTitleOptions([]);
    }
  }, []);

  useEffect(() => {
    loadTitleOptions();
    handleSearch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewHistory = async (offer) => {
    setSelectedOffer(offer);
    setHistoryLoading(true);
    try {
      const data = await offerService.getSendHistory({ offerId: offer.id, page: 0, size: 100 });
      setHistory(data.content ?? []);
    } catch {
      setHistory([]);
      showMsg("error", "Failed to load send history");
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* Search filters */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          🔍 Search Offers
        </h2>
        <div className="p-2">
          <div className="flex flex-wrap gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Offer Title</label>
              <select value={searchTitle}
                onChange={(e) => { setSearchTitle(e.target.value); }}
                className="w-56 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none">
                <option value="">All Offers</option>
                {titleOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setSearchTitle(""); handleSearch(0); }}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
              <i className="fas fa-redo" /> Reset
            </button>
            <button onClick={() => handleSearch(0)} disabled={loading}
              className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
              {loading ? <><i className="fas fa-spinner fa-spin" />Searching...</> : <><i className="fas fa-search" />Search</>}
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          <h2 className="text-base font-bold text-blue-800">
            📋 Offer Details
            {totalElements > 0 && <span className="text-xs font-normal text-blue-600 ml-2">({totalElements} offer{totalElements !== 1 ? "s" : ""})</span>}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-800">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide">
              <tr>
                <th className="py-2 px-4 text-left">Title</th>
                <th className="py-2 px-4 text-center">Valid Till</th>
                <th className="py-2 px-4 text-center">Status</th>
                <th className="py-2 px-4 text-center">Created</th>
                <th className="py-2 px-4 text-center">Send History</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-6"><i className="fas fa-spinner fa-spin text-blue-400 text-2xl" /></td></tr>
              ) : results.length === 0 ? (
                <tr><td colSpan={5} className="py-10 text-center text-gray-400">
                  <i className={`fas ${searched ? "fa-inbox" : "fa-search"} text-4xl mb-3 block`} />
                  <p className="text-sm">{searched ? "No offers found. Try a different title." : "Use the search above to find offers."}</p>
                </td></tr>
              ) : (
                results.map((o, idx) => (
                  <tr key={o.id} className={`border-t transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>
                    <td className="py-1 px-4 font-medium">{o.title}</td>
                    <td className="py-1 px-4 text-center">{o.validTo || "—"}</td>
                    <td className="py-1 px-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${o.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{o.status}</span>
                    </td>
                    <td className="py-1 px-4 text-center text-xs text-gray-500">{o.createdAt ? String(o.createdAt).slice(0, 10) : "—"}</td>
                    <td className="py-1 px-4 text-center">
                      <button onClick={() => viewHistory(o)} title="View send history" className="text-indigo-600 hover:text-indigo-800 font-semibold text-xs">
                        <i className="fas fa-eye mr-1" />View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white">
            <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => handleSearch(0)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => handleSearch(page - 1)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} onClick={() => handleSearch(i)} className={`px-2 py-1 text-xs rounded border ${i === page ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}>{i + 1}</button>
              ))}
              <button onClick={() => handleSearch(page + 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => handleSearch(totalPages - 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          </div>
        )}
      </section>

      {/* Send history for the selected offer */}
      {selectedOffer && (
        <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex-shrink-0">
          <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
            <h2 className="text-base font-bold text-blue-800">
              📨 Sends — {selectedOffer.title}
              {history.length > 0 && <span className="text-xs font-normal text-blue-600 ml-2">({history.length})</span>}
            </h2>
            <button onClick={() => setSelectedOffer(null)} className="text-gray-500 hover:text-gray-700 text-xs">
              <i className="fas fa-times" /> Close
            </button>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="min-w-full text-sm text-gray-800">
              <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="py-2 px-4 text-left">Customer</th>
                  <th className="py-2 px-4 text-left">Mobile</th>
                  <th className="py-2 px-4 text-center">Channel</th>
                  <th className="py-2 px-4 text-center">Status</th>
                  <th className="py-2 px-4 text-center">When</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr><td colSpan={5} className="text-center py-6"><i className="fas fa-spinner fa-spin text-blue-400 text-2xl" /></td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan={5} className="py-8 text-center text-gray-400 text-sm">No send records yet.</td></tr>
                ) : (
                  history.map((h, idx) => (
                    <tr key={h.id} className={`border-t ${idx % 2 === 0 ? "bg-white" : "bg-blue-50"}`}>
                      <td className="py-1 px-4">{h.customerName || "—"}</td>
                      <td className="py-1 px-4 font-mono text-xs">{h.customerMobile}</td>
                      <td className="py-1 px-4 text-center text-xs">{h.channel}</td>
                      <td className="py-1 px-4 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">{h.status}</span>
                      </td>
                      <td className="py-1 px-4 text-center text-xs text-gray-500">
                        {h.createdAt ? String(h.createdAt).replace("T", " ").slice(0, 16) : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default OfferSearch;
